import { useEffect, useState } from "react";
import { Check, Copy, KeyRound, X } from "lucide-react";
import Button from "../ui/Button";

export default function DemoOtpModal({ open, otp, channel = "email", onClose, onUseCode }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    setCopied(false);
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, otp]);

  if (!open || !otp) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(otp);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for restricted clipboard
      const input = document.createElement("input");
      input.value = otp;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-primary/50 p-4 backdrop-blur-sm sm:items-center"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="demo-otp-title"
        className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-primary/[0.06] px-5 py-4">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent/15 text-accent-dark">
              <KeyRound className="h-5 w-5" />
            </span>
            <div>
              <h2 id="demo-otp-title" className="text-lg font-bold text-primary">
                Demo verification code
              </h2>
              <p className="mt-1 text-sm text-primary/55">
                Mock {channel === "phone" ? "SMS" : "email"} — copy this code into the OTP field.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-primary/40 hover:bg-primary/5"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-6">
          <p className="text-center text-[10px] font-bold uppercase tracking-[0.16em] text-primary/40">
            Your 6-digit code
          </p>
          <p className="mt-3 select-all text-center font-mono text-4xl font-bold tracking-[0.35em] text-collect">
            {otp}
          </p>
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-primary/[0.06] px-5 py-4 sm:flex-row sm:justify-end">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
          <Button variant="outline" size="sm" onClick={handleCopy}>
            {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
            {copied ? "Copied" : "Copy code"}
          </Button>
          <Button
            size="sm"
            onClick={() => {
              onUseCode?.(otp);
              onClose();
            }}
          >
            Paste into field
          </Button>
        </div>
      </div>
    </div>
  );
}
